import {
  type LucideIcon,
  DollarSignIcon,
  HandshakeIcon,
  TriangleAlertIcon,
  UserCheckIcon,
} from "lucide-react";

import { Column, Row } from "@/components";
import { Card } from "@/components/ui/card";
import { useGetLeaderboard } from "@/hooks/queries/useGetLeaderboard";
import { DateFilterOption, LeaderboardSort, SortOrder } from "@/types";
import { formatting } from "@/lib/formatting";
import { useMemo } from "react";

export const WorkloadSignalCard = ({
  scopedStafferIds,
  stafferName = null,
  salesName = null,
  jobsWithWarnings,
}: {
  scopedStafferIds: Set<string> | null;
  stafferName?: string | null;
  salesName?: string | null;
  jobsWithWarnings: number;
}) => {
  const leaderboardApi = useGetLeaderboard({
    order: SortOrder.Desc,
    sort: LeaderboardSort.Revenue,
    timePeriod: DateFilterOption.ThisWeek,
  });

  // Sum leaderboard rows scoped to the active staffer/sales filter combination
  const totals = useMemo(() => {
    const t = { outstandingOffers: 0, placements: 0, revenue: 0 };
    for (const user of leaderboardApi.data ?? []) {
      if (scopedStafferIds != null && !scopedStafferIds.has(user.userId.toString())) {
        continue;
      }
      t.outstandingOffers += user.outstandingOffers;
      t.placements += user.placements;
      t.revenue += user.revenue;
    }
    return t;
  }, [leaderboardApi.data, scopedStafferIds]);

  const renderSignal = ({
    icon: Icon,
    label,
    value,
  }: {
    icon: LucideIcon;
    label: string;
    value: string | number;
  }) => {
    // 0 is a real value; only NaN (e.g. missing data) renders as "—".
    const formattedValue =
      typeof value === "number"
        ? Number.isNaN(value)
          ? "—"
          : value.toLocaleString()
        : value;

    return (
      <Row className="h-full min-h-0 w-full justify-center">
        <Column className="h-full w-fit min-w-0 max-w-full justify-end">
          <Column className="h-full min-h-0 min-w-0 justify-between gap-0 text-left">
            <Column className="min-h-0 flex-1 items-start justify-center gap-3">
              <Row className="group/icon relative size-6 shrink-0">
                <span
                  className="absolute inset-0 flex items-center justify-start text-slate-500 transition-opacity [&_svg]:[stroke-width:1.25]"
                  aria-hidden
                >
                  <Icon className="h-6 w-6" strokeWidth={1.25} />
                </span>
              </Row>
              <Row className="items-baseline gap-1.5">
                <p
                  className={
                    "text-xl font-semibold tabular-nums leading-none text-slate-900/80"
                  }
                >
                  {formattedValue}
                </p>
              </Row>
            </Column>
            <Column className="shrink-0 items-start gap-1">
              <p className="text-xs font-medium leading-tight text-slate-500">
                {label}
              </p>
            </Column>
          </Column>
        </Column>
      </Row>
    );
  };

  return (
    <Card className="flex h-full min-h-0 min-w-0 flex-1 flex-col px-5 pb-6 pt-5 shadow-none">
      <Row className="mb-4 h-7 items-center justify-between gap-2">
        <Row className="min-w-0 items-baseline gap-2">
          <p className="text-base font-semibold text-slate-900">
            Workload signal
          </p>
          <p className="truncate text-xs text-slate-500">
            {stafferName
              ? `${stafferName}'s metrics`
              : salesName
                ? `${salesName}'s jobs`
                : "Across all staffers"}
          </p>
        </Row>
      </Row>

      <div className="grid min-h-0 min-w-0 flex-1 auto-rows-fr grid-cols-4 items-stretch justify-items-stretch gap-x-2 gap-y-5">
        {/* Reqs with warnings */}
        {renderSignal({
          icon: TriangleAlertIcon,
          label: "Jobs with warnings",
          value: jobsWithWarnings,
        })}
        {/* Outstanding offers */}
        {renderSignal({
          icon: HandshakeIcon,
          label: "Outstanding offers",
          value: totals.outstandingOffers,
        })}
        {/* Placements this week */}
        {renderSignal({
          icon: UserCheckIcon,
          label: "Placements this week",
          value: totals.placements,
        })}
        {/* Revenue this week */}
        {renderSignal({
          icon: DollarSignIcon,
          label: "Revenue this week",
          value: formatting.currency(totals.revenue),
        })}
      </div>
    </Card>
  );
};
